/**
 * useWebDragScroll — adds mouse-drag scrolling for horizontal paged FlatLists on web.
 *
 * Translates mouse drag into scrollLeft changes on the FlatList's underlying
 * scroll-snap container, with snap-to-page on release. Mimics native FlatList
 * pagingEnabled touch behavior.
 *
 * No-op on native platforms.
 *
 * Usage:
 *   const dragRef = useWebDragScroll({ pageWidth: width, currentPage, pageCount, onPageChange });
 *   <Animated.View ref={dragRef as any} ...>
 *     <FlatList horizontal pagingEnabled ... />
 *   </Animated.View>
 */

import { useRef, useEffect } from "react";
import { Platform } from "react-native";

interface WebDragScrollOptions {
  pageWidth: number;
  currentPage: number;
  pageCount: number;
  onPageChange: (index: number) => void;
  enabled?: boolean;
}

export function useWebDragScroll<T = any>({
  pageWidth,
  currentPage,
  pageCount,
  onPageChange,
  enabled = true,
}: WebDragScrollOptions) {
  const ref = useRef<T>(null);
  const stateRef = useRef({ currentPage, pageCount, pageWidth });
  stateRef.current = { currentPage, pageCount, pageWidth };
  const onPageChangeRef = useRef(onPageChange);
  onPageChangeRef.current = onPageChange;

  useEffect(() => {
    if (Platform.OS !== "web" || !enabled || pageCount === 0 || !pageWidth) return;
    const doc = (globalThis as any).document;
    if (!doc) return;

    let scrollEl: any = null;
    let startX = 0;
    let startScrollLeft = 0;
    let dragging = false;
    let moved = false;

    const findScrollEl = () => {
      if (scrollEl) return scrollEl;
      const container = ref.current as any;
      const domNode: any = container?._node || container;
      if (!domNode?.querySelectorAll) return null;
      const allDivs = domNode.querySelectorAll('div');
      for (let i = 0; i < allDivs.length; i++) {
        if (allDivs[i].scrollWidth > allDivs[i].clientWidth + 10) {
          scrollEl = allDivs[i];
          return scrollEl;
        }
      }
      return null;
    };

    const onDown = (e: any) => {
      const el = findScrollEl();
      if (!el) return;
      startX = e.clientX;
      startScrollLeft = el.scrollLeft;
      dragging = true;
      moved = false;
      el.style.scrollSnapType = 'none';
      el.style.scrollBehavior = 'auto';
      doc.body.style.userSelect = 'none';
    };

    const onMove = (e: any) => {
      if (!dragging || !scrollEl) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 3) moved = true;
      scrollEl.scrollLeft = startScrollLeft - dx;
    };

    const onUp = () => {
      if (!dragging) return;
      dragging = false;
      doc.body.style.userSelect = '';
      if (!scrollEl) return;

      if (!moved) {
        scrollEl.style.scrollSnapType = 'x mandatory';
        scrollEl.style.scrollBehavior = '';
        return;
      }

      const { pageWidth: pw, pageCount: count, currentPage: page } = stateRef.current;
      const targetPage = Math.round(scrollEl.scrollLeft / pw);
      const clamped = Math.max(0, Math.min(count - 1, targetPage));
      scrollEl.style.scrollBehavior = 'smooth';
      scrollEl.style.scrollSnapType = 'x mandatory';
      scrollEl.scrollLeft = clamped * pw;
      setTimeout(() => { if (scrollEl) scrollEl.style.scrollBehavior = ''; }, 400);

      if (clamped !== page) {
        onPageChangeRef.current(clamped);
      }
    };

    const onClick = (e: any) => {
      if (moved) { e.stopPropagation(); e.preventDefault(); moved = false; }
    };

    const timer = setTimeout(() => {
      const el = findScrollEl();
      if (el) {
        el.addEventListener('mousedown', onDown);
        el.addEventListener('click', onClick, true);
      }
    }, 300);

    doc.addEventListener('mousemove', onMove);
    doc.addEventListener('mouseup', onUp);

    return () => {
      clearTimeout(timer);
      doc.removeEventListener('mousemove', onMove);
      doc.removeEventListener('mouseup', onUp);
      if (scrollEl) {
        scrollEl.removeEventListener('mousedown', onDown);
        scrollEl.removeEventListener('click', onClick, true);
        scrollEl.style.scrollSnapType = 'x mandatory';
        scrollEl.style.scrollBehavior = '';
      }
    };
  }, [enabled, pageCount, pageWidth]);

  return ref;
}
